import { ErrorMessage, Field } from 'formik';
import { useState } from 'react';
import { Badge, Button, ButtonGroup, Col, Form, OverlayTrigger, Row, Spinner, Stack, ToggleButton, Tooltip } from 'react-bootstrap';
import { BsInfoCircle } from 'react-icons/bs';
import { IoCloseSharp } from 'react-icons/io5';
import { SchedulerCalendar } from '../../../../../../components/calendar/SchedulerCalendar/SchedulerCalendar';
import ConfirmationModal from '../../../../../../components/modals/ConfirmationModal';
import { FormLabel, FormStepHeader } from '../../../../../../components/forms';
import { TimeField } from '../../../../../../components/tables/filterForms/TimeField';
import { useToast } from '../../../../../../context/ToastContext';
import { useGetVenuesQuery } from '../../../../../../services/endpoints/places/venue';
import { format, parse } from 'date-fns';
import { isValidISODate } from '../../../../../../utils/dateHelpers';
import { getErrorMessage } from '../../../../../../utils/utils';
import { StepProps } from '../AddEditSession';

const daysOfTheWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const classEditNote = 'Session class dates can only be edited in Step 6';

/** Format a no-class badge; skip junk values like "10/12" that crash date-fns format. */
const formatNoClassBadge = (date: string): string | null => {
  if (!isValidISODate(date)) return null;
  return format(parse(date, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy');
};

export const ScheduleForm = ({
  touched,
  errors,
  values,
  setFieldValue,
  setFieldTouched,
  readOnly,
}: StepProps) => {
  const { data, error, isLoading } = useGetVenuesQuery({
    scope: 'active',
    data: { entries: 10000, orderBy: { field: 'title', order: 'asc' } }
  });
  const toast = useToast();
  const [noClassDraft, setNoClassDraft] = useState('');
  const [noClassDateToRemove, setNoClassDateToRemove] = useState<string | null>(null);
  const noClassLocked = Boolean(values?.id);

  const addNoClassDate = (day: string) => {
    if (!day || noClassLocked) return;
    if (!values?.no_class?.includes(day)) {
      setFieldValue!('no_class', [...(values!.no_class ?? []), day]);
    }
    setFieldTouched?.('no_class', true, false);
    setNoClassDraft('');
  };

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  if (error || !data || !data.results) {
    toast.addToast(getErrorMessage(error) ?? 'Error loading venues', 'error');
    return <></>;
  }

  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Schedule" showRequiredLegend={!readOnly} />
      </Col>
      <Col
        xs={12}
        lg={6}
      >
        <Stack gap={3}>
          <Form.Group controlId="day_of_week">
            <FormLabel className="d-flex align-items-center gap-2 mb-1" required>
              Days of the week
              {Boolean(values?.id) && !readOnly && (
                <OverlayTrigger
                  placement="right"
                  trigger={['hover', 'focus']}
                  overlay={<Tooltip id="day-of-week-edit-note">{classEditNote}</Tooltip>}
                >
                  <span
                    tabIndex={0}
                    aria-label={classEditNote}
                    style={{ cursor: 'pointer', color: '#6c757d', display: 'inline-flex', alignItems: 'center' }}
                  >
                    <BsInfoCircle size={14} />
                  </span>
                </OverlayTrigger>
              )}
            </FormLabel>
            <ButtonGroup className="w-100 overflow-auto">
              {daysOfTheWeek.map((day, index) => (
                <Field
                  name="day_of_week"
                  as={Form.Control}
                  key={index}
                  isInvalid={touched.day_of_week && !!errors.day_of_week}
                  disabled={readOnly}
                >
                  {({ field }: any) => (
                    <ToggleButton
                      id={String(index)}
                      type="checkbox"
                      className="fw-normal py-2"
                      variant="outline-secondary"
                      checked={values!.day_of_week.includes(day)}
                      disabled={readOnly}
                      value={day}
                      onChange={() => {
                        const newDaysOfWeek = values!.day_of_week?.includes(day)
                          ? values!.day_of_week.filter((d) => d !== day)
                          : [...values!.day_of_week, day];
                        setFieldValue!(field.name, newDaysOfWeek);
                      }}
                      style={{
                        borderColor: 'rgba(94, 100, 105, .5)',
                        borderWidth: 1,
                        borderLeftWidth: index === 0 ? 1 : 0
                      }}
                    >
                      {day}
                    </ToggleButton>
                  )}
                </Field>
              ))}
            </ButtonGroup>
            {!readOnly && <Form.Text>Select all that apply</Form.Text>}
            <ErrorMessage
              name="day_of_week"
              component="span"
              className="text-danger fs-small"
            />
          </Form.Group>

          <Row>
            <Col
              xs={12}
              lg={6}
            >
              <TimeField
                name="starts_at"
                label="Start Time"
                isInvalid={touched.starts_at && !!errors.starts_at}
                disabled={readOnly}
                required
              />
            </Col>
            <Col
              xs={12}
              lg={6}
            >
              <TimeField
                name="ends_at"
                label="End Time"
                isInvalid={touched.ends_at && !!errors.ends_at}
                disabled={readOnly}
                required
              />
            </Col>
          </Row>

          <Row>
            <Col
              xs={12}
              lg={6}
            >
              <Form.Group
                controlId={'starts_on'}
                className="w-100"
              >
                <FormLabel required>Start Date</FormLabel>
                <Field name={'starts_on'}>
                  {({ field }: { field: any }) => (
                    <Form.Control
                      type="date"
                      {...field}
                      disabled={readOnly}
                      isInvalid={touched.starts_on && !!errors.starts_on}
                      aria-required
                    />
                  )}
                </Field>

                <ErrorMessage
                  name={'starts_on'}
                  component="span"
                  className="text-danger fs-small"
                />
              </Form.Group>
            </Col>
            <Col
              xs={12}
              lg={6}
            >
              <Form.Group
                controlId={'ends_on'}
                className="w-100"
              >
                <FormLabel required>End Date</FormLabel>
                <Field name={'ends_on'}>
                  {({ field }: { field: any }) => (
                    <Form.Control
                      type="date"
                      {...field}
                      disabled={readOnly}
                      isInvalid={touched.ends_on && !!errors.ends_on}
                      aria-required
                    />
                  )}
                </Field>

                <ErrorMessage
                  name={'ends_on'}
                  component="span"
                  className="text-danger fs-small"
                />
              </Form.Group>

              {/* <DateField
                name="ends_on"
                label="End Date"
                isInvalid={touched.ends_on && !!errors.ends_on}
                disabled={readOnly}
              /> */}
            </Col>
          </Row>

          <Form.Group
            controlId="no_class"
            className="w-100"
          >
            <FormLabel className="d-flex align-items-center gap-2 mb-1">
              No Class Dates
              {noClassLocked && !readOnly && (
                <OverlayTrigger
                  placement="right"
                  trigger={['hover', 'focus']}
                  overlay={<Tooltip id="no-class-edit-note">{classEditNote}</Tooltip>}
                >
                  <span
                    tabIndex={0}
                    aria-label={classEditNote}
                    style={{ cursor: 'pointer', color: '#6c757d', display: 'inline-flex', alignItems: 'center' }}
                  >
                    <BsInfoCircle size={14} />
                  </span>
                </OverlayTrigger>
              )}
            </FormLabel>
            {!readOnly && (
              <>
                <Stack
                  direction="horizontal"
                  gap={2}
                >
                  <Form.Control
                    type="date"
                    value={noClassDraft}
                    disabled={noClassLocked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setNoClassDraft(e.target.value);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addNoClassDate(noClassDraft);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    disabled={noClassLocked || !noClassDraft || values?.no_class?.includes(noClassDraft)}
                    onClick={() => addNoClassDate(noClassDraft)}
                  >
                    Add
                  </Button>
                </Stack>

                {!noClassLocked && <Form.Text>Choose a date, then click Add</Form.Text>}
              </>
            )}

            <div>
              {values?.no_class?.map((date, index) => {
                const label = formatNoClassBadge(date);
                if (!label) return null;
                return (
                  <Badge
                    key={index}
                    pill
                    className="text-light me-2 mt-2"
                    style={{ fontSize: '1rem' }}
                  >
                    {label}
                    {!readOnly && (
                      <IoCloseSharp
                        size={16}
                        className="ms-1"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (noClassLocked) {
                            setNoClassDateToRemove(date);
                            return;
                          }
                          setFieldValue!(
                            'no_class',
                            values!.no_class!.filter((d) => d !== date)
                          );
                          setFieldTouched?.('no_class', true, false);
                        }}
                      />
                    )}
                  </Badge>
                );
              })}
            </div>

            {touched.no_class && errors.no_class && (
              <div className="text-danger fs-small mt-1">{errors.no_class}</div>
            )}
            <ConfirmationModal
              show={noClassDateToRemove !== null && formatNoClassBadge(noClassDateToRemove ?? '') !== null}
              title="Remove No Class Date"
              message={
                noClassDateToRemove && formatNoClassBadge(noClassDateToRemove)
                  ? `Remove ${formatNoClassBadge(noClassDateToRemove)} from no-class dates? This clears the label, but it does not add the class back to the session. Add a class card on Step 6 to hold class that day.`
                  : ''
              }
              confirmText="Yes"
              cancelText="Cancel"
              onConfirm={() => {
                if (!noClassDateToRemove) return;
                setFieldValue!(
                  'no_class',
                  values!.no_class!.filter((d) => d !== noClassDateToRemove)
                );
                setFieldTouched?.('no_class', true, false);
                setNoClassDateToRemove(null);
              }}
              onCancel={() => setNoClassDateToRemove(null)}
            />
          </Form.Group>

          <Row className="mb-2">
            <Col
              xs={12}
              lg={6}
              className="mb-2"
            >
              <Form.Group
                controlId={'registration_starts_on'}
                className="w-100"
              >
                <FormLabel>Registration Start Date</FormLabel>
                <Field name={'registration_starts_on'}>
                  {({ field }: { field: any }) => (
                    <Form.Control
                      type="date"
                      {...field}
                      isInvalid={touched.registration_starts_on && !!errors.registration_starts_on}
                      disabled={readOnly}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue!('registration_starts_on', value);
                        if (!value.trim()) {
                          setFieldValue!('registration_starts_at', '');
                        }
                      }}
                    />
                  )}
                </Field>

                <ErrorMessage
                  name="registration_starts_on"
                  component="div"
                  className="text-danger fs-small"
                />
              </Form.Group>
            </Col>
            <Col
              xs={12}
              lg={6}
              className="mb-2"
            >
              <TimeField
                name="registration_starts_at"
                label="Registration Start Time"
                isInvalid={touched.registration_starts_at && !!errors.registration_starts_at}
                disabled={readOnly || !values?.registration_starts_on?.trim()}
              />
            </Col>

            <Col
              xs={12}
              lg={6}
              className="mb-2"
            >
              <Form.Group
                controlId={'registration_ends_on'}
                className="w-100"
              >
                <FormLabel>Registration End Date</FormLabel>
                <Field name={'registration_ends_on'}>
                  {({ field }: { field: any }) => (
                    <Form.Control
                      type="date"
                      {...field}
                      isInvalid={touched.registration_ends_on && !!errors.registration_ends_on}
                      disabled={readOnly}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFieldValue!('registration_ends_on', value);
                        if (!value.trim()) {
                          setFieldValue!('registration_ends_at', '');
                        }
                      }}
                    />
                  )}
                </Field>

                <ErrorMessage
                  name="registration_ends_on"
                  component="div"
                  className="text-danger fs-small"
                />
              </Form.Group>
            </Col>

            <Col
              xs={12}
              lg={6}
              className="mb-2"
            >
              <TimeField
                name="registration_ends_at"
                label="Registration End Time"
                isInvalid={touched.registration_ends_at && !!errors.registration_ends_at}
                disabled={readOnly || !values?.registration_ends_on?.trim()}
              />
            </Col>
          </Row>

          {touched.registration_window && errors.registration_window && (
            <div className="text-danger fs-small mt-2">{errors.registration_window}</div>
          )}
        </Stack>
      </Col>

      <Col
        xs={12}
        lg={6}
      >
        <SchedulerCalendar
          daysOfWeek={
            values?.day_of_week
              .map((day) => daysOfTheWeek.indexOf(day))
              .filter((index) => index >= 0) ?? []
          }
          noClassDates={values?.no_class ?? []}
          classDates={
            values?.id
              ? (values.classes ?? [])
                  .filter((c) => String(c.destroy) !== 'true')
                  .map((c) => c.date)
                  .filter(Boolean)
              : undefined
          }
          startDate={
            values?.starts_on
                ? parse(values.starts_on, 'yyyy-MM-dd', new Date())
                : new Date()
          }
          endDate={
            values?.ends_on
                ? parse(values.ends_on, 'yyyy-MM-dd', new Date())
                : new Date()
          }
        />
      </Col>
    </Row>
  );
};
